import {
  SnackbarVariant,
  Text,
  TextSize,
  TextWeight,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import TextField from '@mui/material/TextField'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Wave } from '../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../hooks/useProjectId'
import { WaveFlowBreadcrumb } from './WaveFlowBreadcrumb'
import { useWaveId } from './useWaveId'
import { useRenameWave, useWaves } from './useWaves'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  card: {
    border: '1px solid #E5EAF5',
    borderRadius: 12,
    padding: 24,
    marginTop: 20,
    maxWidth: 640,
    background: '#FFFFFF'
  },
  field: { marginTop: 20, '&:first-of-type': { marginTop: 0 } },
  label: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#42526E'
  },
  // Boxed like an input without being one — these fields aren't editable.
  value: {
    marginTop: 6,
    border: '1px solid #E5EAF5',
    borderRadius: 8,
    padding: '10px 14px'
  },
  valueMuted: {
    marginTop: 6,
    background: '#F4F5F7',
    borderRadius: 8,
    padding: '10px 14px'
  },
  hint: { marginTop: 6, color: '#6B7280' }
})

const Field = ({
  label,
  value,
  muted
}: {
  label: string
  value: string
  muted?: boolean
}) => {
  const { classes } = useStyles()
  return (
    <div className={classes.field}>
      <Text size={TextSize.s} weight={TextWeight.SemiBold}>
        <span className={classes.label}>{label}</span>
      </Text>
      <div className={muted ? classes.valueMuted : classes.value}>
        <Text size={TextSize.ms}>{value}</Text>
      </div>
    </div>
  )
}

// Numbers are recycled after a soft delete, so walk the list rather than
// taking the previous index.

const previousWave = (waves: Wave[], wave: Wave) =>
  waves
    .filter((candidate) => candidate.number < wave.number)
    .reduce<Wave | undefined>(
      (highest, candidate) =>
        !highest || candidate.number > highest.number ? candidate : highest,
      undefined
    )

/**
 * A single wave, at /project/:projectId/wave/:waveId. "Duplicating from" is
 * derived rather than stored — nothing records what a wave was copied from —
 * and a wave has no target launch date at all.
 */
export const WavePage = () => {
  const { classes } = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const projectId = useProjectId()
  const waveId = useWaveId()
  const { waves, loading, refetch } = useWaves(projectId)
  const { renameWave, loading: renaming } = useRenameWave()
  const wave = waves.find((candidate) => candidate.waveId === waveId)

  const [label, setLabel] = useState('')
  useEffect(() => {
    setLabel(wave?.label ?? '')
  }, [wave?.label])

  // On blur, not per keystroke: renameWave is a full write. Refetched rather
  // than patched, since the breadcrumb and wave list both read the label.
  const handleRename = async () => {
    const trimmed = label.trim()
    if (!wave || trimmed === wave.label) {
      return
    }
    if (!trimmed) {
      setLabel(wave.label)
      return
    }
    try {
      await renameWave(wave.waveId, trimmed)
      await refetch()
    } catch (error) {
      setLabel(wave.label)
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to rename wave',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  if (loading) {
    return (
      <div>
        <WaveFlowBreadcrumb activeStep="Wave" />
        <div className={classes.page}>
          <Text size={TextSize.ms}>Loading…</Text>
        </div>
      </div>
    )
  }

  // A wave id that isn't this project's goes back to the project.
  if (!wave) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  const previous = previousWave(waves, wave)

  return (
    <div>
      <WaveFlowBreadcrumb activeStep="Wave" />

      <div className={classes.page}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          Wave
        </Text>

        <div className={classes.card}>
          <div className={classes.field}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              <span className={classes.label}>Wave label</span>
            </Text>
            <TextField
              fullWidth
              size="small"
              value={label}
              disabled={renaming}
              onChange={(event) => setLabel(event.target.value)}
              onBlur={handleRename}
              inputProps={{ 'aria-label': 'Wave label' }}
            />
            <div className={classes.hint}>
              <Text size={TextSize.s}>
                Defaults to the wave number — rename it freely (e.g. “Q3 2026”).
              </Text>
            </div>
          </div>
          <Field
            label="Duplicating from"
            value={
              previous
                ? `Wave ${previous.number} — ${previous.label}`
                : 'First wave — nothing to duplicate from'
            }
            muted
          />
          <Field label="Target launch date" value="—" muted />
        </div>
      </div>
    </div>
  )
}
